
import { Component, Vue } from 'vue-property-decorator';
import {
  StockyMigrationModel,
  StockySupplierPreview,
  StockySupplierImportResult,
  StockyPOJobStatus,
} from '@/models/stocky_migration';

@Component
export default class StockyMigrationWizard extends Vue {
  private step = 0;
  private loading = false;
  private error: string | null = null;

  // Step 0: Connection
  private stockyApiKey = '';
  private isConnected = false;
  private hasSavedKey = false;
  private savedKeyMask = '';

  // Step 1: Supplier preview
  private supplierPreview: StockySupplierPreview | null = null;
  private supplierConflictResolutions: Record<number, string> = {};
  private supplierImportResult: StockySupplierImportResult | null = null;
  private isSuppliersImported = false;

  // Step 2: PO status selection
  private selectedPOStatuses: string[] = [
    'draft-unarchived',
    'confirmed-unarchived',
    'draft-archived',
    'confirmed-archived',
  ];
  private poStatusOptions = [
    { value: 'confirmed-unarchived', label: 'Active', description: 'Confirmed, partially received, and received POs', icon: 'mdi-check-circle', color: 'success' },
    { value: 'draft-unarchived', label: 'Draft', description: 'POs still in draft status', icon: 'mdi-pencil-circle', color: 'grey' },
    { value: 'confirmed-archived', label: 'Archived (Confirmed)', description: 'Completed and archived confirmed POs', icon: 'mdi-archive', color: 'teal' },
    { value: 'draft-archived', label: 'Archived (Draft)', description: 'Archived draft POs', icon: 'mdi-archive-outline', color: 'blue-grey' },
  ];

  // Step 3: PO import
  private jobId: string | null = null;
  private jobStatus: StockyPOJobStatus | null = null;
  private pollingInterval: number | null = null;

  private stepLabels = ['Connect', 'Suppliers', 'Select POs', 'Import'];

  private migrationFeatures = [
    'Suppliers and contact information',
    'Tax types and rates',
    'All purchase orders (draft, confirmed, received, archived)',
    'PO line items matched to your Shopify products',
    'Receive history',
    'Supplier-product relationships',
  ];

  private conflictResolutionOptions = [
    { text: 'Skip', value: 'skip' },
    { text: 'Update', value: 'update' },
    { text: 'Keep Both', value: 'keep_both' },
  ];

  private supplierTableHeaders = [
    { text: 'Name', value: 'name' },
    { text: 'Email', value: 'contactEmail' },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Action', value: 'action', sortable: false },
  ];


  async mounted() {
    try {
      const status = await StockyMigrationModel.getConnection();
      if (status.hasKey) {
        this.hasSavedKey = true;
        this.savedKeyMask = status.maskedKey || '';
        this.isConnected = true;
      }
    } catch {
      // Ignore — user will enter key manually
    }
  }

  get currentTitle(): string {
    switch (this.step) {
      case 0: return 'Migrate from Stocky';
      case 1: return 'Migrate from Stocky — Suppliers';
      case 2: return 'Migrate from Stocky — Select POs';
      case 3: return 'Migrate from Stocky — Import';
      default: return 'Migrate from Stocky';
    }
  }

  get isImportRunning(): boolean {
    return this.jobStatus !== null
      && (this.jobStatus.status === 'processing' || this.jobStatus.status === 'pending');
  }

  get isImportDone(): boolean {
    return this.jobStatus !== null
      && (this.jobStatus.status === 'completed' || this.jobStatus.status === 'error');
  }

  get showActionButton(): boolean {
    if (this.step === 3 && this.isImportDone) return false;
    if (this.step === 3 && this.isImportRunning) return false;
    return true;
  }

  get actionButtonText(): string {
    switch (this.step) {
      case 0:
        return this.isConnected ? 'Next' : 'Test Connection';
      case 1:
        return this.isSuppliersImported ? 'Next' : 'Import Suppliers';
      case 2:
        return 'Import Purchase Orders';
      default:
        return 'Next';
    }
  }

  get isActionDisabled(): boolean {
    if (this.loading) return true;
    if (this.step === 0 && !this.isConnected && !this.stockyApiKey.trim()) return true;
    if (this.step === 2 && this.selectedPOStatuses.length === 0) return true;
    return false;
  }

  stepClass(idx: number): string {
    if (idx < this.step) return 'step-indicator--done';
    if (idx === this.step) return 'step-indicator--active';
    return 'step-indicator--pending';
  }

  handleEsc() {
    if (!this.isImportRunning) {
      this.$emit('close');
    }
  }

  goBack() {
    if (this.step > 0) {
      this.step -= 1;
    }
  }

  togglePOStatus(value: string) {
    const idx = this.selectedPOStatuses.indexOf(value);
    if (idx >= 0) {
      this.selectedPOStatuses.splice(idx, 1);
    } else {
      this.selectedPOStatuses.push(value);
    }
  }

  async handleAction() {
    this.error = null;

    if (this.step === 0) {
      if (this.isConnected) {
        this.loading = true;
        this.step = 1;
        await this.fetchSupplierPreview();
      } else {
        await this.testConnection();
      }
      return;
    }

    if (this.step === 1) {
      if (this.isSuppliersImported) {
        this.step = 2;
      } else {
        await this.importSuppliers();
      }
      return;
    }

    if (this.step === 2) {
      this.step = 3;
      await this.startPOImport();
    }
  }

  async testConnection() {
    this.loading = true;
    this.error = null;
    try {
      await StockyMigrationModel.connect(this.stockyApiKey);
      this.isConnected = true;
    } catch (err: any) {
      this.error = err.message || 'Failed to connect to Stocky. Please check your API key.';
    } finally {
      this.loading = false;
    }
  }

  async fetchSupplierPreview() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierPreview = await StockyMigrationModel.previewSuppliers();
      // Initialize conflict resolutions with default "skip"
      if (this.supplierPreview) {
        for (const supplier of this.supplierPreview.suppliers) {
          if (supplier.conflict) {
            this.$set(this.supplierConflictResolutions, supplier.stockyId, 'skip');
          }
        }
      }
    } catch (err: any) {
      this.error = err.message || 'Failed to load supplier preview.';
    } finally {
      this.loading = false;
    }
  }

  async importSuppliers() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierImportResult = await StockyMigrationModel.importSuppliers(
        this.supplierConflictResolutions
      );
      this.isSuppliersImported = true;
    } catch (err: any) {
      this.error = err.message || 'Failed to import suppliers.';
    } finally {
      this.loading = false;
    }
  }

  async startPOImport() {
    this.error = null;
    try {
      const job = await StockyMigrationModel.importPurchaseOrders(this.selectedPOStatuses);
      this.jobId = job.jobId;
      this.jobStatus = { status: 'pending' };
      this.startPolling(job.jobId);
    } catch (err: any) {
      this.error = err.message || 'Failed to start purchase order import.';
    }
  }

  startPolling(jobId: string) {
    this.pollingInterval = window.setInterval(async () => {
      try {
        const status = await StockyMigrationModel.getImportStatus(jobId);
        this.jobStatus = status;

        if (status.status === 'completed' || status.status === 'error') {
          this.stopPolling();
        }
      } catch (err) {
        this.stopPolling();
        this.error = 'Failed to get import status.';
      }
    }, 2000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  navigateTo(routeName: string) {
    this.$emit('close');
    this.$router.push({ name: routeName }).catch(() => {});
  }

  beforeDestroy() {
    this.stopPolling();
  }
}
